import { femaleQuestionMedium, maleQuestionMedium } from '@/assets/icons';
import {
  HAVING_NO_PROBLEMS,
  HAVING_NO_PROBLEMS_SOURCE,
  WASTE,
  WASTE_SOURCE,
  WITHOUT_DATA,
  WITHOUT_DATA_SOURCE,
} from './quotes';

const WHAT_IS_A_PB = `A problem is a <strong>discrepancy</strong> between the <strong>current</strong> situation and an <strong>ideal</strong> situation.`;

const SPOT_PB = `
<div class="ia-sensei-quote">
  <span class="quote">"${HAVING_NO_PROBLEMS}"</span>
  <br>
  <span class="author">${HAVING_NO_PROBLEMS_SOURCE}</span>
</div>
<h2 class="basic-sub-title">
  What is a problem?
</h2>
<p>
  Let's get back to the basics for a second :<br>
  @:sensei.WHAT_IS_A_PB<br>
  The ideal situation can be a standard or an indicator objective, for example.
  In other words, a problem is an opportunity to improve the current situation and to make something better.<br>
  Monitoring daily indicators of performance (or KPI) is a great starting point to spotting problems.
  A KPI exceeding the objective is a synonym of a problem as there is a visible gap between the objective line and the exceeding values.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Ok, but not having problems would mean that the situation is at its best, right?</span>
</div>
<p>
  Or that people should look harder. Look around and ask yourself <i>"Is everything in its ideal state ?"</i> or <i>"Could anything be improved in my daily work routine ?"</i><br>
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${maleQuestionMedium}"></path></svg>
  <span class="question">What if I think that something could be improved, but I feel like it's not my place to mention it?</span>
</div>
<p>
  The easy answer is : Use <strong>data</strong>. When reporting a problem within Shizen, you have the opportunity to fill in multiple fields.
  The 'How much' field is actually a reminder that one should always use precise data to define a problem.<br>
  Let's remember that a problem is a discrepancy between the current situation and an ideal situation : That means that
  when reading a problem's description, anyone should be able to understand how big is the gap between the ideal and the current states.<br>
  For that, one needs data.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${WITHOUT_DATA}"</span>
  <br>
  <span class="author">${WITHOUT_DATA_SOURCE}</span>
</div>
<h2 class="basic-sub-title">
  Types of waste
</h2>
<p>
  Understanding the main types of wastes may help you identify these problems.<br>
  We call these wastes "Muda", there are 7 major types :
  <ul>
    <li>
      Overproduction : Or producing in too large a quantity or at the wrong time. This necessarily leads to other wastes listed below: It creates stocks, implies moving stocks, which could results in additional lead times or defects.
    </li>
    <li>
      Waiting : This appears when an employee has to wait for the result of a machine, for example. We can then assume that the \"flow\" can be optimized, i. e. we can rethink the sequence of process steps.
    </li>
    <li>
      Transport: A transport muda comes from the fact that related areas are too distant. For example, shop A produces a set of parts to be sent to shop B. The two workshops are so far apart that trolleys must be driven from one workshop to another.
    </li>
    <li>
      Process: Production process in a team or workshop is not useful for the customer. For example, a part is produced with an incorrect size, which forces the customer to throw it away or process it. This type of problem will be encountered when the client's need is not well understood (or described). It is necessary to establish in a precise way the customer's needs in order to choose the steps of the production process.
    </li>
    <li>
      Stock : This is often the result of an overproduction phenomenon. This implies stocks of unusable objects (products are damaged or outdated). The supply may need to be reviewed to avoid a too large quantity and therefore difficult to manage. If stocks are necessary as in the case of a logistics warehouse, it may be the storage system that needs improvement.
    </li>
    <li>
      Movement: Not to be confused with transport. Here, waste implies that a person (or machine) is forced to make difficult movements to reach tools, materials, etc.
    </li>
    <li>
      Defects: A common quality problem that occurs as a result of a poorly controlled production process or poor transmission of indications.
    </li>
  </ul>
  Note that an additional types of Muda can be added sometimes. But you should probably focus on these 7 types to begin with.
  <div class="ia-sensei-quote">
    <span class="quote">"${WASTE}"</span>
    <br>
    <span class="author">${WASTE_SOURCE}</span>
  </div>
</p>
`;

const SPOT_PB_TITLE = 'Spotting a problem';

export default {
  SPOT_PB,
  SPOT_PB_TITLE,
  WHAT_IS_A_PB,
};
